import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Heart, Home, Shield, CheckCircle2 } from 'lucide-react';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';

const AboutPage = () => {
  return (
    <div className="about-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner */}
      <ReviewBanner />
      
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">About Maid for Cats</h1>
          <p className="page-subtitle">Professional, caring, and trustworthy cat sitting in Walsall</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="our-story-section">
        <div className="container">
          {/* Rob and Clare Photo */}
          <div className="about-team-photo">
            <img 
              src="https://customer-assets.emergentagent.com/job_eb1246c6-a55c-45ff-ad7f-691bbbc2d9d7/artifacts/t89vddph_14290.png" 
              alt="Rob and Clare - Maid for Cats owners with their cat" 
              className="team-photo-image"
            />
            <p className="team-photo-caption">Rob and Clare - Your dedicated cat sitters</p>
          </div>

          <div className="story-content">
            <div className="story-text">
              <h2 className="section-title">About Us – MAID for Cats</h2>
              <p className="story-paragraph">
                We're a dedicated husband-and-wife team behind MAID for Cats, and caring for cats isn't just our business — it's been a huge part of our lives for many years.
              </p>
              <p className="story-paragraph">
                With years of hands-on experience looking after cats of all personalities, ages, and needs, we understand that every cat is unique. From the shy and nervous to the playful and affectionate, we take the time to get to know each cat and provide care that suits them purrfectly.
              </p>
              <p className="story-paragraph">
                Being a family-run business means everything we do is personal. When you choose MAID for Cats, you're not dealing with a large company or rotating staff — it's us. The same familiar faces visiting your home, building trust with your cat, and giving you complete peace of mind while you're away.
              </p>
              <p className="story-paragraph">
                We believe cats are happiest in their own environment, surrounded by their familiar sights, smells, and routines. That's why we're passionate about providing a reliable, loving home-visit service that keeps your cat comfortable, safe, and stress-free.
              </p>
              <p className="story-paragraph">
                Over time, we've built strong relationships with both our clients and their cats, and we take great pride in the trust placed in us every single day.
              </p>
              <p className="story-paragraph">
                Whether it's a short trip or a longer holiday, you can count on us to care for your cat as if they were our own.
              </p>
              <p className="story-signature">
                Thank you for considering us,<br />
                <strong>Rob and Clare</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="values-section">
        <div className="container">
          <h2 className="section-title">What We Stand For</h2>
          
          <div className="values-grid">
            <div className="value-card">
              <Heart className="value-icon" />
              <h3>Compassionate Care</h3>
              <p>Every cat deserves gentle, patient, and loving attention. We treat your cats as if they were our own, with kindness and respect at all times.</p>
            </div>

            <div className="value-card">
              <Shield className="value-icon" />
              <h3>Trust & Reliability</h3>
              <p>We understand you're entrusting us with your home and your beloved companions. We take that responsibility seriously and always show up when we say we will.</p>
            </div>

            <div className="value-card">
              <Home className="value-icon" />
              <h3>Local & Independent</h3>
              <p>As a local independent business based in Walsall, we're part of your community. We're here for the long term, building lasting relationships with our clients.</p>
            </div>

            <div className="value-card">
              <CheckCircle2 className="value-icon" />
              <h3>Peace of Mind</h3>
              <p>From daily photo updates to our Home Guardians service, everything we do is designed to give you complete confidence and peace of mind while you're away.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why In-Home Care */}
      <section className="why-inhome-section">
        <div className="container">
          <div className="why-content-grid">
            <div className="why-text">
              <h2 className="section-title">Why We Believe in In-Home Care</h2>
              <p className="why-paragraph">
                Cats are territorial animals who feel safest in their own environment. Unlike dogs, who often enjoy new experiences and locations, cats prefer the familiar sights, sounds, and smells of home.
              </p>
              <p className="why-paragraph">
                Taking a cat to a cattery can be stressful — from the car journey to the unfamiliar surroundings and the presence of other animals. This stress can affect their eating, sleeping, and overall wellbeing.
              </p>
              <p className="why-paragraph">
                With in-home care, your cats can maintain their normal routine, sleep in their favourite spots, and enjoy their meals without disruption. They receive one-on-one attention in a calm, familiar space, which means less stress and a happier cat.
              </p>
              <p className="why-paragraph">
                Plus, it's often more affordable than a cattery — and you get the added benefit of our Home Guardians service to keep your property secure.
              </p>
            </div>

            <div className="why-image">
              <img src="https://images.pexels.com/photos/18188816/pexels-photo-18188816.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Content cat at home" />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Credentials */}
      <section className="credentials-section">
        <div className="container">
          <h2 className="section-title">Your Trust is Earned</h2>
          <p className="section-intro">We're committed to the highest standards of professionalism and safety</p>
          
          <div className="credentials-grid">
            <div className="credential-item">
              <div className="credential-icon-wrapper">
                <Shield className="credential-icon" />
              </div>
              <h3>Fully Insured</h3>
              <p>We carry comprehensive insurance to protect both your cats and your home during our visits.</p>
            </div>

            <div className="credential-item">
              <div className="credential-icon-wrapper">
                <CheckCircle2 className="credential-icon" />
              </div>
              <h3>DBS Checked</h3>
              <p>We've undergone thorough background checks for your complete peace of mind and security.</p>
            </div>

            <div className="credential-item">
              <div className="credential-icon-wrapper">
                <Heart className="credential-icon" />
              </div>
              <h3>Free Meet & Greet</h3>
              <p>Before your first booking, we offer a free, no-obligation meet and greet so you can get to know us and see if we're the right fit.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="commitment-section">
        <div className="container">
          <div className="commitment-box">
            <h2>Our Commitment to You</h2>
            <div className="commitment-list">
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>We'll always arrive on time for scheduled visits</span>
              </div>
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>You'll receive a photo and message update every single day</span>
              </div>
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>We'll respect your home and follow your instructions carefully</span>
              </div>
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>We'll contact you immediately if there are any concerns about your cats' health</span>
              </div>
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>We'll provide the same consistent, caring sitter for every visit</span>
              </div>
              <div className="commitment-item">
                <CheckCircle2 className="commitment-check" />
                <span>We'll treat your cats with the patience, gentleness, and love they deserve</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Let's Meet Your Cats</h2>
            <p>We'd love to meet you and your feline family. Book a free meet and greet to see if we're the right fit.</p>
            <Link to="/contact">
              <Button size="lg" className="cta-button-large">
                Book Your Free Meet & Greet
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
